import { z } from 'zod'
import { CAPABILITIES, type Capability } from './capabilities'
import { GOVERNANCE_OFFICES, OFFICE_LABELS, type GovernanceOffice } from './governance'
import { WORKER_TYPES, WORKER_TYPE_LABELS, type WorkerType } from './workforce'

// Hub access by position (ADR 0071). A rule says a position carries a
// capability; Platform Core grants it to whoever holds the position, tied to
// the record that says they hold it, and revokes it when that record ends.
// Grants given by hand are never touched.

export const ACCESS_POSITIONS_SYSTEM_SUB = 'system:access-positions'

export const POSITION_KINDS = [
  'director',
  'officer',
  'executive_committee',
  'committee_chair',
  'committee_member',
  'workforce',
  'department_lead',
  'shift_lead',
  'qualification',
] as const
export type PositionKind = (typeof POSITION_KINDS)[number]

export const POSITION_KIND_LABELS: Record<PositionKind, string> = {
  director: 'Director',
  officer: 'Officer',
  executive_committee: 'Executive Committee member',
  committee_chair: 'Committee chair',
  committee_member: 'Committee member',
  workforce: 'Staff or volunteer',
  department_lead: 'Department lead',
  shift_lead: 'Shift lead',
  qualification: 'Qualification',
}

// Never given by position: administration, granting access, anything
// confidential or sensitive, BRE accounts, and anything that moves money.
// The database refuses them too (core migration 0087).
export const NEVER_BY_POSITION: readonly Capability[] = [
  'hub.admin',
  'access.manage',
  'notes.confidential',
  'documents.confidential',
  'governance.confidential',
  'records.confidential',
  'records.privileged',
  'workforce.sensitive',
  'auth.accounts.manage',
  'auth.accounts.override',
  'rewards.adjust',
  'rewards.grant',
  'giftcards.resend',
  'rentals.money',
  'refunds.approve',
  'events.settle',
  'settlements.approve',
  'finance.manage',
  'purchasing.manage',
]

export function isDerivableCapability(capability: string): capability is Capability {
  return capability in CAPABILITIES && !NEVER_BY_POSITION.includes(capability as Capability) && !capability.endsWith('.confidential') && !capability.startsWith('auth.accounts.') && !capability.startsWith('ledger.')
}

export const DERIVABLE_CAPABILITIES = (Object.keys(CAPABILITIES) as Capability[]).filter(isDerivableCapability)

// Which position a rule names. office for an officer; committee_id narrows a
// chair or member to one committee (none: any committee); worker_type for
// staff or volunteers; department_id narrows a department lead; a
// qualification type for a qualification.
export const PositionSpec = z
  .object({
    position: z.enum(POSITION_KINDS),
    office: z.enum(GOVERNANCE_OFFICES).nullable().optional(),
    committeeId: z.string().min(1).max(80).nullable().optional(),
    workerType: z.enum(WORKER_TYPES).nullable().optional(),
    departmentId: z.string().min(1).max(80).nullable().optional(),
    qualificationTypeId: z.string().min(1).max(80).nullable().optional(),
  })
  .superRefine((s, ctx) => {
    const need = (ok: boolean, message: string) => ok || ctx.addIssue({ code: 'custom', message })
    need((s.position === 'officer') === !!s.office, 'An officer rule names the office, and only an officer rule does')
    need(!s.committeeId || s.position === 'committee_chair' || s.position === 'committee_member', 'Only a committee rule names a committee')
    need((s.position === 'workforce') === !!s.workerType, 'A staff or volunteer rule names the type, and only that rule does')
    need(!s.departmentId || s.position === 'department_lead', 'Only a department lead rule names a department')
    need((s.position === 'qualification') === !!s.qualificationTypeId, 'A qualification rule names the qualification, and only that rule does')
  })
export type PositionSpec = z.infer<typeof PositionSpec>

export const PositionRuleBody = z.intersection(
  PositionSpec,
  z.object({ capabilities: z.array(z.string().refine(isDerivableCapability, 'Not given by position')).min(1).max(40) }),
)
export type PositionRuleBody = z.infer<typeof PositionRuleBody>

export type PositionRule = {
  id: string
  position: PositionKind
  office: GovernanceOffice | null
  committeeId: string | null
  workerType: WorkerType | null
  departmentId: string | null
  qualificationTypeId: string | null
  // "President", "Chair: Finance Committee", "Volunteer", ...
  label: string
  capability: Capability
  createdAt: string
  // Current grants this rule holds.
  holders: number
  // What reconciling now would do.
  wouldGrant: number
  wouldRevoke: number
}

export type PositionRunCounts = { ruleId: string; label: string; capability: string; granted: number; revoked: number }

export type PositionRunResult = {
  enabled: boolean
  // Off: what would be granted and revoked, nothing written but the counts.
  dryRun: boolean
  granted: number
  revoked: number
  rules: PositionRunCounts[]
  // Revocations of grants whose rule is gone (removed rules) are counted
  // under their own rule id too.
  ranAt: string
}

export type PositionAccessView = {
  enabled: boolean
  rules: PositionRule[]
  lastRun: PositionRunResult | null
  options: {
    offices: { value: GovernanceOffice; label: string }[]
    committees: { id: string; name: string }[]
    departments: { id: string; name: string }[]
    qualificationTypes: { id: string; name: string }[]
  }
}

export function positionLabel(
  spec: { position: PositionKind; office?: GovernanceOffice | null; workerType?: WorkerType | null },
  names: { committee?: string | null; department?: string | null; qualification?: string | null } = {},
): string {
  switch (spec.position) {
    case 'officer':
      return spec.office ? OFFICE_LABELS[spec.office] : 'Officer'
    case 'committee_chair':
      return names.committee ? `Chair: ${names.committee}` : 'Chair of a committee'
    case 'committee_member':
      return names.committee ? `Member: ${names.committee}` : 'Member of a committee'
    case 'workforce':
      return spec.workerType ? WORKER_TYPE_LABELS[spec.workerType] : 'Staff or volunteer'
    case 'department_lead':
      return names.department ? `Lead: ${names.department}` : 'Lead of a department'
    case 'qualification':
      return names.qualification ? `Qualified: ${names.qualification}` : 'Qualification'
    default:
      return POSITION_KIND_LABELS[spec.position]
  }
}
